import React, { useState, useEffect } from 'react';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';

import './metrics_below_map.css';

// Opciones para el filtro de severidad
const severeOptions = [
  { value: 'Light', label: 'Light' },
  { value: 'Severe', label: 'Severe' },
  { value: 'Lightning', label: 'Lightning' },
  { value: 'High_Density_Lightning', label: 'High-density lightning' },
  { value: 'Overshoot', label: 'Overshoot' },
];

const MetricsBelowMap = ({ metricsNames, cqlFecha, weekNumber, issueTime, cqlSevere, handleRiskClick }) => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Estado para controlar si se usan las métricas mediante el endpoint n1m
  const [useN1M, setUseN1M] = useState(false);
  // Estado para el filtro de severidad (por defecto se usa cqlSevere o "Light")
  const [selectedSevere, setSelectedSevere] = useState('Light');


  // Función para remover el sufijo _n1m de las claves de un objeto
  const removeN1mSuffix = (data) => {
    return Object.fromEntries(
      Object.entries(data).map(([key, value]) => {
        // Si la clave termina con "_n1m", se remueve ese sufijo
        const newKey = key.endsWith('_n1m') ? key.slice(0, -4) : key;
        return [newKey, value];
      })
    );
  };

  // Función para formatear valores numéricos como porcentajes
  const formatPercentage = (value) => {
    if (typeof value === 'number') {
      return `${(value * 1).toFixed(3)}`;
    }
    return value;
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    const endpoint = useN1M
      ? '/api/get_metrics_below_map_n1m'
      : '/api/get_metrics_below_map';
    const url = `${process.env.REACT_APP_API_URL}${endpoint}?cqlFecha=${encodeURIComponent(cqlFecha)}&cqlSevere=${encodeURIComponent(selectedSevere)}&weekNumber=${encodeURIComponent(weekNumber)}&metricsNames=${encodeURIComponent(metricsNames)}&issueTime=${encodeURIComponent(issueTime)}`;
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      // console.log('Data:', data);
      const processedData = useN1M
      ? data.map((item) => removeN1mSuffix(item))
      : data;
      setMetrics(processedData);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  // Ejecutamos la petición cada vez que cambian los parámetros relevantes
  useEffect(() => {
    if (cqlFecha && selectedSevere && weekNumber && metricsNames && issueTime) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [metricsNames, cqlFecha, selectedSevere, weekNumber, issueTime, useN1M]);

  if (error) {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="error" align="center">
          Error al cargar las métricas.
        </Typography>
      </Box>
    );
  }

  // Se asume que la respuesta es un array con al menos un objeto
  const metricsData = Array.isArray(metrics) && metrics.length > 0 ? metrics[0] : {};

  return (
    <Paper
      elevation={2}
      sx={{
        p: 2,
        // maxWidth: useN1M ? 720 : 570,
        maxWidth: 570,
        mx: 'auto',
        mt: 2,
        borderRadius: 2,
      }}
    >
      {/* Controles superiores: checkbox y Autocomplete */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <input
            type="checkbox"
            id="toggleN1M"
            checked={useN1M}
            onChange={() => setUseN1M(!useN1M)}
            style={{ width: 14, height: 14, marginRight: 4 }}
          />
          <Typography variant="body2">Show Neighbourhood Metrics</Typography>
        </Box>
        <Select
            value={cqlSevere}
            onChange={(e) => handleRiskClick(e.target.value)}
            sx={{
              fontSize: 'inherit',
              marginLeft: '2.5px',
              marginRight: '-12px',
              fontWeight: 'bold',
              color: 'blue',
              backgroundColor: 'transparent',
              '.MuiSelect-select': {
                padding: 0,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
            }}
            disableUnderline
          >
            {/* Opciones de riesgo */}
            <MenuItem value="Light">Light</MenuItem>
            <MenuItem value="Severe">Severe</MenuItem>
            <MenuItem value="Lightning">Lightning</MenuItem>
            <MenuItem value="High_Density_Lightning">High-density lightning</MenuItem>
            <MenuItem value="Overshoot">Overshoot</MenuItem>
          </Select>
      </Box>

      {/* Contenedor de métricas en "chips" */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          justifyContent: 'center',
        }}
      >
        {Object.entries(metricsData).map(([label, value]) => (
          <Box
            key={label}
            sx={{
              display: 'flex',
              alignItems: 'center',
              px: 1,
              py: 0.5,
              border: '1px solid #e2e8f0',
              borderRadius: '4px',
              bgcolor: '#f1f5f9',
            }}
          >
            <Typography variant="caption" sx={{ color: '#6c757d', mr: 0.5 }}>
              {label}:
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#1d283a' }}>
              {formatPercentage(value)}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* {loading && (
        <Typography variant="body2" align="center" sx={{ mt: 2 }}>
          Actualizando...
        </Typography>
      )} */}
    </Paper>
  );
};

export default MetricsBelowMap;
